import { useEffect, useRef } from "react";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "[tabindex]",
]
  .map((selector) => `${selector}:not([hidden]):not([tabindex="-1"])`)
  .join(",");

const activeTrapStack = [];

const removeTrap = (token) => {
  const index = activeTrapStack.lastIndexOf(token);
  if (index !== -1) activeTrapStack.splice(index, 1);
};

/** Keep Tab inside an open dialog without relying on layout visibility. */
export const useFocusTrap = (containerRef, active = true) => {
  const tokenRef = useRef(Symbol("focus-trap"));

  useEffect(() => {
    if (!active) return undefined;

    const token = tokenRef.current;
    removeTrap(token);
    activeTrapStack.push(token);

    const onKeyDown = (event) => {
      if (event.key !== "Tab") return;
      if (activeTrapStack[activeTrapStack.length - 1] !== token) return;
      const node = containerRef.current;
      if (!node) return;

      const items = Array.from(node.querySelectorAll(FOCUSABLE));
      if (!items.length) {
        event.preventDefault();
        node.focus();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;

      if (!node.contains(current)) {
        event.preventDefault();
        first.focus();
        return;
      }
      // THE CONTAINER ITSELF, and it needs its own branch because every other
      // one silently declines it. A DOM node contains itself, so
      // `node.contains(node)` is TRUE and the outside branch above cannot fire;
      // and the container is never in `items` (it carries `tabIndex="-1"`, which
      // FOCUSABLE excludes), so it is neither `first` nor `last` and neither
      // wrap branch below fires either. Tab then fell through to native
      // behaviour and walked straight out of the dialog into the page behind
      // it. Reachable whenever a dialog root is focusable, which every dialog
      // here is: `SquadDrawer`'s `<aside tabIndex={-1}>` takes focus from a
      // direct click on its own padding.
      if (current === node) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }
      if (event.shiftKey && current === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      removeTrap(token);
    };
  }, [containerRef, active]);
};

export default useFocusTrap;
